import { Button, ConfirmModal } from '../../../../components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModalToggle } from '../../../../hooks';
import { FormProvider } from 'react-hook-form';
import { appRoutes } from '../../../../config';
import clsx from 'clsx';
import { useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

import type { UseFormReturn } from 'react-hook-form';
import type { IToggle } from '../../../../hooks';
import type { IStep } from '../../../../types';
import type { FC } from 'react';

export interface IButtonBarProps {
    activeStep: number;
    disableButtonToggle: IToggle;
    formContext: UseFormReturn;
    isSaving: boolean;
    isSavingAndQuiting: boolean;
    onComplete: () => void;
    onNext: () => void;
    onSaveAndQuit: () => void;
    prevStep: () => void;
    steps: Array<IStep>;
}

export const ButtonBar: FC<IButtonBarProps> = ({
    activeStep,
    disableButtonToggle,
    formContext,
    isSaving,
    isSavingAndQuiting,
    onComplete,
    onNext,
    onSaveAndQuit,
    prevStep,
    steps,
}) => {
    const location = useLocation();
    const navigate = useNavigate();

    const { selectedLanguage } = useContext(LanguageSelectorContext);
    const localization = new Localization(selectedLanguage);

    const onNextClick = () => {
        formContext.trigger().then((isValid: boolean) => {
            if (!isValid) {
                const formErrors = formContext.formState.errors;
                let highestError: HTMLElement | null = null;
                let index = '';
                Object.keys(formErrors).forEach((key) => {
                    const formElement = formErrors[key]?.ref as HTMLElement;
                    if (formElement && (!highestError || formElement.offsetTop > highestError.offsetTop)) {
                        highestError = formElement;
                        index = key;
                    }
                });

                if (index) {
                    const formElement = formErrors[index]?.ref as HTMLElement;
                    if (formElement.scrollIntoView) formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    if (formElement.focus) formElement.focus();
                }
            } else if (activeStep !== steps.length - 1) onNext();
            else confirmSubmitModalToggle.open();
        });
    };

    const onCancelClick = () => {
        if (location.pathname === appRoutes.dashboard) navigate(appRoutes.dashboard);
        else confirmDashboardNavigateModalToggle.open();
    };

    const onCancelConfirmed = () => navigate(appRoutes.dashboard);

    const confirmSubmitModalToggle = useModalToggle();
    const confirmSaveAndQuitModalToggle = useModalToggle();
    const confirmDashboardNavigateModalToggle = useModalToggle();

    const isDisabled: boolean = isSaving || isSavingAndQuiting || disableButtonToggle.isOn;

    return (
        <FormProvider {...formContext}>
            <div className={'p-5'}>
                <div className={'flex flex-col items-center'}>
                    <div className={'grid gap-2 xxs:grid-cols-2 sm:grid-cols-4'}>
                        {/* CANCEL */}
                        <Button
                            buttonProps={{
                                isDisabled: isDisabled,
                                onPress: onCancelClick,
                            }}
                            variant={'ghost'}
                            className={'w-36'}
                        >
                            {localization.translate('buttonBar.exitButton')}
                        </Button>

                        {/* SAVE AND QUIT */}
                        <Button
                            buttonProps={{
                                isDisabled: isDisabled,
                                onPress: confirmSaveAndQuitModalToggle.open,
                            }}
                            isBusy={isSavingAndQuiting}
                            variant={'ghost'}
                            className={'w-36'}
                        >
                            {localization.translate('buttonBar.saveAndQuitButton')}
                        </Button>

                        {/* PREV BUTTON */}
                        <Button
                            buttonProps={{
                                isDisabled: activeStep === 0 || isDisabled,
                                onPress: prevStep,
                            }}
                            className={'group w-36'}
                            variant={'outline'}
                        >
                            <div className={'flex w-full flex-row items-center transition duration-200 ease-in-out'}>
                                {activeStep !== 0 && (
                                    <span
                                        aria-hidden={'true'}
                                        className={clsx(
                                            '-mt-0.5 inline-block translate-x-0 transition-transform duration-200 ease-in-out',
                                            !isDisabled && activeStep !== 0 && 'group-hover:-translate-x-1'
                                        )}
                                    >
                                        &lt;
                                    </span>
                                )}
                                &nbsp;{localization.translate('buttonBar.previousButton')}
                            </div>
                        </Button>

                        {/* NEXT/FINISH BUTTON */}
                        <Button
                            buttonProps={{
                                isDisabled: isDisabled,
                                onPress: onNextClick,
                            }}
                            className={'group w-36'}
                            isBusy={isSaving}
                        >
                            <div
                                className={
                                    'flex w-full flex-row items-center justify-center transition duration-200 ease-in-out'
                                }
                            >
                                {activeStep === steps.length - 1
                                    ? localization.translate('buttonBar.completeButton')
                                    : localization.translate('buttonBar.nextButton')}
                                &nbsp;
                                {activeStep !== steps.length - 1 && (
                                    <span
                                        aria-hidden={'true'}
                                        className={clsx(
                                            '-mt-0.5 inline-block translate-x-0 transition-transform duration-200 ease-in-out',
                                            { 'group-hover:translate-x-1': !isDisabled }
                                        )}
                                    >
                                        &gt;
                                    </span>
                                )}
                            </div>
                        </Button>
                    </div>
                </div>
            </div>

            {/* CONFIRM SUBMIT CLAIM */}
            <ConfirmModal
                message={localization.translate('buttonBar.confirmSubmitMessage')}
                modalToggle={confirmSubmitModalToggle}
                onConfirm={onComplete}
                title={localization.translate('buttonBar.confirmSubmitTitle')}
            />

            {/* CONFIRM SAVE AND QUIT */}
            <ConfirmModal
                message={localization.translate('buttonBar.confirmSaveAndQuitMessage')}
                modalToggle={confirmSaveAndQuitModalToggle}
                onConfirm={onSaveAndQuit}
                title={localization.translate('buttonBar.confirmSaveAndQuitTitle')}
            />

            {/* CONFIRM CANCEL */}
            <ConfirmModal
                message={localization.translate('buttonBar.confirmCancelMessage')}
                modalToggle={confirmDashboardNavigateModalToggle}
                onConfirm={onCancelConfirmed}
                title={localization.translate('buttonBar.confirmCancelTitle')}
            />
        </FormProvider>
    );
};